import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, IconButton, Collapse } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import {
  useGetFeederGroupsQuery,
  useAddFeederGroupMutation,
  useUpdateFeederGroupMutation,
  useDeleteFeederGroupMutation,
} from "../UserSettings/scheduleApiSlice";

const LABEL_SX = {
  fontSize: "0.62rem",
  fontWeight: 600,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.3)",
  mb: "6px",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#0d1117",
    borderRadius: "6px",
    fontSize: "0.88rem",
    color: "#e0e6f0",
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.22)" },
    "&.Mui-focused fieldset": { borderColor: "#FDB940 !important", borderWidth: "1px !important" },
  },
  "& .MuiInputBase-input": { padding: "8px 12px" },
};

export default function FeederGroupManager() {
  const { data: feederGroups = [], isSuccess } = useGetFeederGroupsQuery();
  const [addFeederGroup] = useAddFeederGroupMutation();
  const [updateFeederGroup] = useUpdateFeederGroupMutation();
  const [deleteFeederGroup] = useDeleteFeederGroupMutation();
  const [deletingId, setDeletingId] = useState(null);

  const handleAdd = () => {
    addFeederGroup({ body: { name: "New Feeder Group", description: "" } });
  };

  const handleSave = (group) => {
    updateFeederGroup({ id: group.id, body: { name: group.name, description: group.description } });
  };

  const handleDelete = (id) => {
    deleteFeederGroup({ id });
    setDeletingId(null);
  };

  if (!isSuccess) return null;

  return (
    <Box>
      {/* Section header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: "20px" }}>
        <Box>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
            Feeder Groups
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.4)", mt: "3px" }}>
            Operator-level groupings of feeders. Referenced by every schedule.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: "1rem !important", color: "#0d1117" }} />}
          onClick={handleAdd}
          sx={{
            bgcolor: "#FDB940",
            color: "#0d1117",
            fontWeight: 600,
            fontSize: "0.82rem",
            textTransform: "none",
            borderRadius: "8px",
            boxShadow: "none",
            px: "14px",
            py: "7px",
            "&:hover": { bgcolor: "#e8a830", boxShadow: "none" },
          }}
        >
          Add Feeder Group
        </Button>
      </Box>

      {feederGroups.length === 0 && (
        <Typography sx={{ textAlign: "center", py: 4, color: "rgba(255,255,255,0.35)", fontSize: "0.88rem" }}>
          No feeder groups yet. Click "Add Feeder Group" to create one.
        </Typography>
      )}

      {feederGroups.map((group) => (
        <FeederGroupRow
          key={group.id}
          group={group}
          deletingId={deletingId}
          setDeletingId={setDeletingId}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      ))}
    </Box>
  );
}

function FeederGroupRow({ group, deletingId, setDeletingId, onSave, onDelete }) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setName(group.name);
    setDescription(group.description);
    setDirty(false);
  }, [group]);

  const handleChange = (field, value) => {
    if (field === "name") setName(value);
    else setDescription(value);
    setDirty(true);
  };

  return (
    <Box sx={{ mb: "8px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: "12px",
          backgroundColor: "#161B22",
          border: dirty
            ? "1px solid rgba(253,185,64,0.45)"
            : "1px solid rgba(255,255,255,0.07)",
          borderRadius: "10px",
          px: "20px",
          py: "14px",
          transition: "border-color 0.15s",
        }}
      >
        {/* ID */}
        <Box sx={{ width: 52, flexShrink: 0 }}>
          <Typography sx={LABEL_SX}>ID</Typography>
          <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.65)", fontWeight: 500, lineHeight: "34px" }}>
            {group.id}
          </Typography>
        </Box>

        {/* NAME */}
        <Box sx={{ width: 260, flexShrink: 0 }}>
          <Typography sx={LABEL_SX}>NAME</Typography>
          <TextField
            value={name}
            onChange={(e) => handleChange("name", e.target.value)}
            fullWidth
            size="small"
            sx={INPUT_SX}
          />
        </Box>

        {/* DESCRIPTION */}
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={LABEL_SX}>DESCRIPTION</Typography>
          <TextField
            value={description}
            onChange={(e) => handleChange("description", e.target.value)}
            fullWidth
            size="small"
            sx={INPUT_SX}
          />
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: "2px", flexShrink: 0, alignSelf: "flex-end" }}>
          <IconButton
            size="small"
            disabled={!dirty}
            onClick={() => { onSave({ id: group.id, name, description }); setDirty(false); }}
            sx={{
              color: dirty ? "#FDB940" : "rgba(255,255,255,0.2)",
              "&:hover": { bgcolor: dirty ? "rgba(253,185,64,0.1)" : "transparent" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.18)" },
            }}
          >
            <SaveIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setDeletingId(group.id)}
            sx={{
              color: "rgba(255,255,255,0.3)",
              "&:hover": { color: "#f87171", bgcolor: "rgba(248,113,113,0.08)" },
            }}
          >
            <DeleteIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>
        </Box>
      </Box>

      {/* Inline delete confirmation */}
      <Collapse in={deletingId === group.id}>
        <Box
          sx={{
            mt: "4px",
            px: "20px",
            py: "12px",
            backgroundColor: "rgba(248,113,113,0.06)",
            border: "1px solid rgba(248,113,113,0.2)",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.65)" }}>
            Delete <strong style={{ color: "#e0e6f0" }}>"{group.name}"</strong>? This cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Button
              size="small"
              onClick={() => setDeletingId(null)}
              sx={{
                fontSize: "0.8rem",
                textTransform: "none",
                color: "rgba(255,255,255,0.5)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={() => onDelete(group.id)}
              sx={{
                fontSize: "0.8rem",
                textTransform: "none",
                color: "#f87171",
                border: "1px solid rgba(248,113,113,0.35)",
                borderRadius: "6px",
                px: "10px",
                "&:hover": { bgcolor: "rgba(248,113,113,0.1)", borderColor: "#f87171" },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
